import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, fmtDate } from '../api';
import { ROLE_LABEL, useAuth } from '../auth';
import { Badge, Button, Card, Empty, ErrorBox, Field, Flash, inputCls, Loading, PageHeader, RiskBadge, StateBadge, useAction, useApi } from '../components/ui';
import { MediaPanel, MediaThumb } from '../components/MediaPanel';

type Body = {
  text: string;
  headline?: string;
  seo?: { slug: string; metaDescription: string; keywords: string[]; faq: { q: string; a: string }[]; internalLinks: string[] };
  cta: string | null;
  link: string | null;
  hashtags: string[];
  claims: { text: string; source_url: string | null; kind: string }[];
  hypotheses: string[];
  visualBrief?: string;
  media: { assetFileId: string; checksum: string; alt?: string }[];
  scheduledAt: string | null;
  budgetBRL: number | null;
};

/** Diff de linhas (LCS) entre duas versões. */
function lineDiff(a: string, b: string): { kind: 'same' | 'add' | 'del'; text: string }[] {
  const A = a.split('\n');
  const B = b.split('\n');
  const dp = Array.from({ length: A.length + 1 }, () => new Array<number>(B.length + 1).fill(0));
  for (let i = A.length - 1; i >= 0; i--) for (let j = B.length - 1; j >= 0; j--) dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: { kind: 'same' | 'add' | 'del'; text: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < A.length && j < B.length) {
    if (A[i] === B[j]) {
      out.push({ kind: 'same', text: A[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ kind: 'del', text: A[i++] });
    else out.push({ kind: 'add', text: B[j++] });
  }
  while (i < A.length) out.push({ kind: 'del', text: A[i++] });
  while (j < B.length) out.push({ kind: 'add', text: B[j++] });
  return out;
}

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  return parts.replace(' ', 'T');
};
/** Converte "AAAA-MM-DDTHH:mm" (São Paulo, UTC−3 sem horário de verão desde 2019) em ISO UTC. */
const fromLocalInput = (v: string) => (v ? new Date(`${v}:00-03:00`).toISOString() : null);

/** Publicação assistida: copiar o texto aprovado, publicar no canal e registrar a URL real do post. */
function ManualPublish({ assetId, channel, finalText, media, canConfirm, onDone }: { assetId: string; channel: string; finalText: string | null; media: string[]; canConfirm: boolean; onDone: () => void }) {
  const act = useAction();
  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);
  return (
    <section className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 shadow-sm" aria-labelledby="manual-title">
      <h2 id="manual-title" className="text-base font-semibold text-amber-900">
        Publicar agora — publicação assistida
      </h2>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-800">
        <li>Copie o texto aprovado abaixo (exatamente como está; não edite no LinkedIn).</li>
        <li>Publique na {channel === 'linkedin_company' ? 'página da 3S Networks no LinkedIn' : 'conta indicada'}.</li>
        <li>Abra o post publicado, copie o link e cole aqui para registrar.</li>
      </ol>
      <label className="mt-3 block text-sm font-medium" htmlFor="final-text">
        Texto aprovado
      </label>
      <textarea id="final-text" readOnly className={`${inputCls} mt-1 font-mono text-xs`} rows={10} value={finalText ?? ''} />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(finalText ?? '').then(() => setCopied(true));
          }}
        >
          {copied ? 'Texto copiado ✓' : 'Copiar texto'}
        </Button>
        {media.map((id, i) => (
          <a key={id} className="inline-flex items-center rounded-md px-3 py-2 text-sm text-brand-700 underline" href={`/api/media/${id}?download=1`}>
            Baixar mídia {media.length > 1 ? i + 1 : ''} ↓
          </a>
        ))}
        {channel.startsWith('linkedin') && (
          <a className="inline-flex items-center rounded-md px-3 py-2 text-sm text-brand-700 underline" href="https://www.linkedin.com/feed/" target="_blank" rel="noreferrer noopener">
            Abrir LinkedIn ↗
          </a>
        )}
      </div>
      {canConfirm ? (
        <div className="mt-3 space-y-2">
          <ErrorBox error={act.error} />
          <Field label="Link do post publicado" hint="Ex.: https://www.linkedin.com/feed/update/urn:li:activity:…">
            <input className={inputCls} type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <Button variant="success" disabled={act.busy || url.length < 10} onClick={() => void act.run(() => api(`/api/assets/${assetId}/manual-publish`, { method: 'POST', body: { url } }), 'Publicação registrada.').then(onDone)}>
            Confirmar publicação
          </Button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-700">Seu papel não registra publicações. Avise o Sponsor de Marketing.</p>
      )}
    </section>
  );
}

export function AssetDetail() {
  const { id } = useParams();
  const { me, can } = useAuth();
  const { data, error, loading, reload } = useApi<any>(`/api/assets/${id}`, [id]);
  const act = useAction();
  const [comment, setComment] = useState('');
  const [signAs, setSignAs] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ text: string; cta: string; link: string; scheduledAt: string; changeNote: string } | null>(null);
  const [feedback, setFeedback] = useState('');
  const [compare, setCompare] = useState<number | null>(null);

  const versions = data?.versions ?? [];
  const current = versions[0];
  const body: Body | undefined = current?.body;
  useEffect(() => {
    if (data?.canApproveAs?.length && !signAs) setSignAs(data.canApproveAs[0]);
  }, [data, signAs]);

  const tech = data?.reviews.find((r: any) => r.kind === 'technical' && r.version === current?.version);
  const comp = data?.reviews.find((r: any) => r.kind === 'compliance' && r.version === current?.version);
  const brand = data?.reviews.find((r: any) => r.kind === 'brand' && r.version === current?.version);
  const diff = useMemo(() => {
    const other = versions.find((v: any) => v.version === compare);
    return other && current ? lineDiff(other.body.text, current.body.text) : null;
  }, [compare, versions, current]);

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const a = data.asset;
  const pending = a.state === 'PENDING_HUMAN_APPROVAL';
  const canDecide = pending && can('approval:decide') && data.canApproveAs.length > 0;
  const editable = can('content:edit') && ['CHANGES_REQUESTED', 'PENDING_HUMAN_APPROVAL', 'APPROVED', 'SCHEDULED', 'BLOCKED', 'EXPIRED', 'PUBLISH_FAILED'].includes(a.state);
  const decide = (decision: string) =>
    act.run(() => api(`/api/assets/${a.id}/decision`, { method: 'POST', body: { decision, signAs, comment: comment || undefined, expectedVersion: a.currentVersion } }), `Decisão registrada: ${decision}`).then(reload);

  return (
    <>
      <PageHeader
        title={`${a.id} · ${a.channel}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StateBadge state={a.state} /> <RiskBadge risk={a.riskClass} /> v{a.currentVersion} · demanda{' '}
            <Link className="text-brand-700 underline" to={`/demandas/${a.requestId}`}>
              {a.requestId}
            </Link>
          </span>
        }
        actions={<Button variant="secondary" onClick={reload}>Atualizar</Button>}
      />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      {a.stateReason && <p className="mb-4 rounded-md bg-slate-100 p-3 text-sm text-slate-800">Motivo do estado: {a.stateReason}</p>}

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          <Card title={`Prévia real (v${current?.version ?? '—'})`} actions={<span className="font-mono text-[11px] text-slate-500" title="hash do conteúdo aprovado">{a.currentHash?.slice(0, 12)}</span>}>
            {body ? (
              <article className="rounded-lg border border-slate-200 p-4" aria-label="Prévia da publicação">
                <div className="mb-2 flex items-center gap-2">
                  <img src="/brand/3s-logo.svg" alt="" aria-hidden className="h-10 w-10 rounded border border-slate-200 bg-white object-contain p-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold">3S Networks</div>
                    <div className="text-xs text-slate-500">Agendado: {fmtDate(body.scheduledAt)} (São Paulo)</div>
                  </div>
                </div>
                <p className="whitespace-pre-line text-sm leading-relaxed">{body.text}</p>
                {body.link && (
                  <p className="mt-2 break-all text-sm">
                    <a className="text-brand-700 underline" href={body.link} target="_blank" rel="noreferrer noopener">
                      {body.link}
                    </a>
                  </p>
                )}
                <p className="mt-2 text-sm text-brand-700">{body.hashtags.join(' ')}</p>
                {body.media?.length ? (
                  <div className="mt-3 space-y-2">
                    {body.media.map((m) => {
                      const f = data.media.find((x: any) => x.id === m.assetFileId);
                      return f ? <MediaThumb key={m.assetFileId} file={f} className="w-full rounded border border-slate-200" /> : null;
                    })}
                    {body.media[0].alt && <p className="text-xs text-slate-500">Alt: {body.media[0].alt}</p>}
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-slate-500">Mídia: nenhuma anexada (post só com texto). Use “Mídia da peça” abaixo.</p>
                )}
              </article>
            ) : (
              <Empty>Sem versão gerada.</Empty>
            )}
          </Card>

          {editing && draft ? (
            <Card title="Editar (Studio)">
              <p role="note" className="mb-3 rounded bg-amber-50 p-2 text-xs text-amber-900">
                Salvar cria uma nova versão, invalida aprovações existentes, cancela agendamentos pendentes e reenvia para revisão técnica e compliance.
              </p>
              <div className="space-y-3">
                <Field label="Texto" required>
                  <textarea className={inputCls} rows={12} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
                </Field>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="CTA">
                    <input className={inputCls} value={draft.cta} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} />
                  </Field>
                  <Field label="Horário (São Paulo)">
                    <input className={inputCls} type="datetime-local" value={draft.scheduledAt} onChange={(e) => setDraft({ ...draft, scheduledAt: e.target.value })} />
                  </Field>
                  <Field label="Link">
                    <input className={inputCls} type="url" value={draft.link} onChange={(e) => setDraft({ ...draft, link: e.target.value })} />
                  </Field>
                  <Field label="Nota da alteração" required>
                    <input className={inputCls} value={draft.changeNote} onChange={(e) => setDraft({ ...draft, changeNote: e.target.value })} />
                  </Field>
                </div>
                <div className="flex gap-2">
                  <Button
                    disabled={act.busy || draft.changeNote.length < 3}
                    onClick={() =>
                      act
                        .run(
                          () =>
                            api(`/api/assets/${a.id}/edit`, {
                              method: 'POST',
                              body: { text: draft.text, cta: draft.cta || null, link: draft.link || null, scheduledAt: fromLocalInput(draft.scheduledAt), changeNote: draft.changeNote },
                            }),
                          'Nova versão salva e enviada para revisão.',
                        )
                        .then((ok) => {
                          if (ok) setEditing(false);
                          return reload();
                        })
                    }
                  >
                    Salvar nova versão
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}

          <MediaPanel
            assetId={a.id}
            media={data.media}
            attachedIds={(body?.media ?? []).map((m) => m.assetFileId)}
            creative={data.creative}
            generation={data.mediaGeneration}
            canEdit={can('content:edit')}
            onChange={reload}
          />

          <Card title="Versões e diferenças">
            <ul className="space-y-1 text-sm">
              {versions.map((v: any) => (
                <li key={v.version} className="flex flex-wrap items-center gap-2">
                  <strong>v{v.version}</strong> <Badge>{v.authorKind === 'agent' ? v.authorId : 'humano'}</Badge>
                  <span className="text-xs text-slate-600">{fmtDate(v.createdAt)} · {v.changeNote}</span>
                  {v.version !== current.version && (
                    <button className="text-xs text-brand-700 underline" onClick={() => setCompare(compare === v.version ? null : v.version)}>
                      {compare === v.version ? 'ocultar diff' : `comparar com v${current.version}`}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {diff && (
              <pre className="mt-3 max-h-80 overflow-auto rounded bg-slate-50 p-2 text-xs" aria-label="Diferenças entre versões">
                {diff.map((d, i) => (
                  <div key={i} className={d.kind === 'add' ? 'bg-emerald-100 text-emerald-900' : d.kind === 'del' ? 'bg-red-100 text-red-900 line-through' : ''}>
                    {d.kind === 'add' ? '+ ' : d.kind === 'del' ? '− ' : '  '}
                    {d.text}
                  </div>
                ))}
              </pre>
            )}
          </Card>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card title="Aprovação">
            <p className="text-sm">
              Exigências ({a.riskClass}):{' '}
              {(a.requiredRoles as string[]).length ? (a.requiredRoles as string[]).map((r) => (
                <Badge key={r} tone={data.approvalStatus.satisfied.includes(r) ? 'green' : 'amber'}>
                  {ROLE_LABEL[r] ?? r} {data.approvalStatus.satisfied.includes(r) ? '✓' : '…'}
                </Badge>
              )) : '—'}
            </p>
            {canDecide ? (
              <div className="mt-3 space-y-2">
                <Field label="Assinar como">
                  <select className={inputCls} value={signAs} onChange={(e) => setSignAs(e.target.value)}>
                    {data.canApproveAs.map((r: string) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r] ?? r}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Comentário">
                  <textarea className={inputCls} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
                </Field>
                <p className="text-xs text-slate-600">
                  Você aprova a versão v{a.currentVersion} exatamente como exibida na prévia (hash {a.currentHash?.slice(0, 12)}).
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="success" disabled={act.busy} onClick={() => decide('APPROVED')}>
                    Aprovar
                  </Button>
                  <Button variant="secondary" disabled={act.busy || comment.length < 3} onClick={() => decide('CHANGES_REQUESTED')} title="Exige comentário">
                    Solicitar alteração
                  </Button>
                  <Button variant="danger" disabled={act.busy || comment.length < 3} onClick={() => decide('REJECTED')} title="Exige comentário">
                    Rejeitar
                  </Button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-600">
                {pending ? `Seu papel (${ROLE_LABEL[me?.user.role ?? '']}) não atende às exigências desta peça.` : 'Não há aprovação pendente nesta etapa.'}
              </p>
            )}
            <h3 className="mt-4 text-xs font-semibold uppercase text-slate-500">Trilha de aprovação</h3>
            <ul className="mt-1 space-y-1 text-xs">
              {data.approvals.length ? (
                data.approvals.map((x: any) => (
                  <li key={x.id} className={x.status === 'INVALIDATED' ? 'text-slate-400 line-through' : ''}>
                    v{x.version} · {x.decision} como {ROLE_LABEL[x.role] ?? x.role} por {x.approverId} · {fmtDate(x.createdAt)}
                    {x.comment ? ` — “${x.comment}”` : ''} {x.status === 'INVALIDATED' && <span className="no-underline">(invalidada: {x.invalidatedReason})</span>}
                  </li>
                ))
              ) : (
                <li>Nenhuma decisão.</li>
              )}
            </ul>
          </Card>

          <Card title="Fontes e claims (A07)">
            {tech ? (
              <>
                <Badge tone={tech.verdict === 'APROVADO' ? 'green' : tech.verdict === 'BLOQUEADO' ? 'red' : 'amber'}>{tech.verdict}</Badge>
                {tech.findings.requires3sEngineer && <Badge tone="amber">requer engenheiro 3S</Badge>}
                <ul className="mt-2 space-y-1 text-xs">
                  {tech.findings.claims.map((c: any, i: number) => (
                    <li key={i}>
                      <Badge tone={c.deterministicStatus === 'verified' ? 'green' : c.deterministicStatus === 'rejected' ? 'red' : 'slate'}>{c.deterministicStatus}</Badge> {c.text}{' '}
                      {c.source_url && (
                        <a className="break-all text-brand-700 underline" href={c.source_url} target="_blank" rel="noreferrer noopener">
                          {c.source_url}
                        </a>
                      )}{' '}
                      {c.deterministicReason && <span className="text-red-800">({c.deterministicReason})</span>}
                    </li>
                  ))}
                  {!tech.findings.claims.length && <li>Sem claims factuais (texto opinativo/técnico geral).</li>}
                  {tech.findings.findings.map((f: any, i: number) => (
                    <li key={`f${i}`} className={f.severity === 'bloqueante' || f.severity === 'alta' ? 'text-red-900' : 'text-slate-700'}>
                      <Badge tone={f.severity === 'bloqueante' || f.severity === 'alta' ? 'red' : f.severity === 'media' ? 'amber' : 'slate'}>{f.severity ?? 'nota'}</Badge> {f.issue}: “{f.evidence}” → {f.suggestion}
                    </li>
                  ))}
                  {tech.findings.notes?.length > 0 && tech.verdict === 'APROVADO' && (
                    <li className="mt-1 rounded bg-amber-50 p-2 text-amber-900">
                      Aprovado tecnicamente com {tech.findings.notes.length} nota(s) média/baixa acima: avalie antes de aprovar ou edite a peça.
                    </li>
                  )}
                </ul>
              </>
            ) : (
              <Empty>Sem revisão técnica para a versão atual.</Empty>
            )}
            {body?.hypotheses?.length ? (
              <div className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">Hipóteses: {body.hypotheses.join(' · ')}</div>
            ) : null}
          </Card>

          <Card title="Risco e compliance (A20)">
            {comp ? (
              <>
                <Badge tone={comp.verdict.startsWith('LIBERADO') ? 'green' : comp.verdict === 'BLOQUEADO' ? 'red' : 'amber'}>{comp.verdict}</Badge>
                <ul className="mt-2 list-disc pl-5 text-xs">
                  {comp.findings.findings.map((f: any, i: number) => (
                    <li key={i}>
                      {f.severity && <Badge tone={f.severity === 'bloqueante' || f.severity === 'alta' ? 'red' : f.severity === 'media' ? 'amber' : 'slate'}>{f.severity}</Badge>} {f.category}: {f.issue} — {f.evidence}
                    </li>
                  ))}
                  {comp.findings.notes?.length > 0 && comp.verdict.startsWith('LIBERADO') && (
                    <li className="mt-1 list-none rounded bg-amber-50 p-2 text-amber-900">
                      Liberado com {comp.findings.notes.length} nota(s): confirme os pontos marcados como “media” antes de aprovar.
                    </li>
                  )}
                </ul>
                <p className="mt-2 text-xs text-slate-600">{comp.findings.approverNotes}</p>
              </>
            ) : (
              <Empty>Ainda não avaliado.</Empty>
            )}
          </Card>

          {brand && (
            <Card title="Marca e posicionamento (A05)">
              <Badge tone={brand.verdict === 'CONSISTENTE' ? 'green' : 'amber'}>{brand.verdict === 'CONSISTENTE' ? 'Consistente' : 'Ajustar'}</Badge>{' '}
              <span className="text-xs text-slate-600">aderência {Math.round(brand.findings.score)}/100 · consultivo: só achado grave vai ao A20</span>
              {brand.findings.findings.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-xs">
                  {brand.findings.findings.map((f: any, i: number) => (
                    <li key={i}>
                      <Badge tone={f.severity === 'alta' ? 'red' : f.severity === 'media' ? 'amber' : 'slate'}>{f.severity}</Badge> “{f.excerpt}”: {f.issue} — <span className="text-slate-600">{f.suggestion}</span>
                    </li>
                  ))}
                </ul>
              )}
              {brand.findings.pillarsUsed?.length > 0 && <p className="mt-2 text-xs text-slate-600">Pilares presentes: {brand.findings.pillarsUsed.join(', ')}</p>}
            </Card>
          )}

          {body?.seo && (
            <Card title="SEO e AEO do artigo (A08)">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-slate-500">Endereço</dt>
                <dd>
                  <code>/{body.seo.slug}</code>
                </dd>
                <dt className="text-slate-500">Meta description</dt>
                <dd>
                  {body.seo.metaDescription} <span className="text-slate-500">({body.seo.metaDescription.length}/155)</span>
                </dd>
                <dt className="text-slate-500">Palavras-chave</dt>
                <dd>{body.seo.keywords.join(', ')}</dd>
                <dt className="text-slate-500">Links internos</dt>
                <dd>{body.seo.internalLinks.join(' · ') || '—'}</dd>
              </dl>
              {body.seo.faq.length > 0 && (
                <>
                  <h3 className="mt-3 text-xs font-semibold uppercase text-slate-500">Perguntas frequentes</h3>
                  <ul className="mt-1 space-y-1 text-xs">
                    {body.seo.faq.map((q: any, i: number) => (
                      <li key={i}>
                        <strong>{q.q}</strong> {q.a}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          )}

          <Card title="Publicação">
            {data.publishJobs.length ? (
              <ul className="space-y-1 text-xs">
                {data.publishJobs.map((p: any) => (
                  <li key={p.id}>
                    v{p.version} · {fmtDate(p.scheduledAt)} · <StateBadge state={p.status} /> · tentativas {p.attempts} {p.simulated && <Badge tone="violet">SIMULAÇÃO</Badge>}
                    {p.lastError && <div className="text-red-800">{p.lastError}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>Nada agendado. Agendamento só após aprovação completa.</Empty>
            )}
            {a.publishedUrl && (
              <p className="mt-2 text-sm">
                Publicado em {fmtDate(a.publishedAt)}:{' '}
                {a.publishSimulated ? (
                  <>
                    <code className="break-all">{a.publishedUrl}</code> <Badge tone="violet">SIMULADO — não está no LinkedIn</Badge>
                  </>
                ) : (
                  <a className="break-all text-brand-700 underline" href={a.publishedUrl} target="_blank" rel="noreferrer noopener">
                    {a.publishedUrl}
                  </a>
                )}
              </p>
            )}
          </Card>

          {data.awaitingManualPublish && <ManualPublish assetId={a.id} channel={a.channel} finalText={data.finalText} media={(body?.media ?? []).map((m) => m.assetFileId)} canConfirm={can('content:schedule')} onDone={reload} />}

          {(editable || can('content:cancel')) && (
            <Card title="Ações">
              <div className="flex flex-wrap gap-2">
                {editable && body && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDraft({ text: body.text, cta: body.cta ?? '', link: body.link ?? '', scheduledAt: toLocalInput(body.scheduledAt), changeNote: '' });
                      setEditing(true);
                    }}
                  >
                    Editar no Studio
                  </Button>
                )}
                {can('content:cancel') && !['CANCELED', 'PUBLISHED', 'ARCHIVED', 'REJECTED'].includes(a.state) && (
                  <Button variant="danger" disabled={act.busy} onClick={() => act.run(() => api(`/api/assets/${a.id}/cancel`, { method: 'POST', body: { reason: 'Cancelado pelo usuário no painel' } }), 'Peça cancelada.').then(reload)}>
                    Cancelar peça
                  </Button>
                )}
              </div>
              {['CHANGES_REQUESTED', 'BLOCKED'].includes(a.state) && can('content:edit') && (
                <div className="mt-3 space-y-2">
                  <Field label="Pedir nova versão ao A09 (orientações)">
                    <textarea className={inputCls} rows={2} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
                  </Field>
                  <Button disabled={act.busy || feedback.length < 3} onClick={() => act.run(() => api(`/api/assets/${a.id}/redraft`, { method: 'POST', body: { feedback: [feedback] } }), 'Reescrita solicitada; acompanhe o estado.').then(reload)}>
                    Pedir nova versão
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>

        <Card title="Histórico" className="lg:col-span-5">
          <ol className="max-h-72 space-y-1 overflow-auto text-xs">
            {data.timeline.map((e: any) => (
              <li key={e.id} className="font-mono">
                {fmtDate(e.at)} · {e.actorId} · {e.action} {e.data?.from ? `${e.data.from} → ${e.data.to}` : ''} {e.data?.reason ? `· ${e.data.reason}` : ''}
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}
